import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { DateTime } from "luxon";
import useAmazonDeliveryMetricsXlsxExport, { UseAmazonDeliveryMetricsXlsxExportContext } from "../hooks/useAmazonDeliveryMetricsXlsxExport";
import useAmazonFedExDeliveryMetricsGet, { UseAmazonFedExDeliveryMetricsGetContext } from "../hooks/useAmazonFedExDeliveryMetricsGet";
import useAmazonFedExDeliveryMetricsPost, { UseAmazonFedExDeliveryMetricsPostContext } from "../hooks/useAmazonFedExDeliveryMetricsPost";
import { fedexDelayReason } from "../utilities/fedexDelayReasons";
import { DeliveryMetrics } from "../../common/common.types";
import LoadingScreen from '../../loading/LoadingComponent';
import { useUserContext } from '../../user/UserContext';
import { getPreviousWeekRange } from '../utilities/getPreviousWeekRage';

// Create the context
const AmazonDeliveryMetricsContext = createContext(null);

interface AmazonDeliveryMetricsProviderContext {
  selectedStartDate,
  setSelectedStartDate,
  selectedEndDate,
  setSelectedEndDate,
  setStartDate,
  setEndDate,
  isLoading,
  loadingStep,
  loadingSubStep,
  currentError,
  deliveryMetrics,
  handleFileChange,
  uploadFile,
  handleAmazonDeliveryMetricsDetailedExport,
  handleAmazonDeliveryMetricsTableExport,
  selectedReason,
  setSelectedReason,
  filteredData,
  handleFilter,
  startDate,
  endDate
}

// Context Provider
export const AmazonDeliveryMetricsProvider = ({ children }) => {
  const {userData} = useUserContext()

  const [selectedStartDate, setSelectedStartDate] = useState(getPreviousWeekRange().previousSunday);
  const [selectedEndDate, setSelectedEndDate] = useState(getPreviousWeekRange().previousSaturday);
  const [selectedReason, setSelectedReason] = useState("")
  const [file, setFile] = useState<File | null>(null)
  const [startDate, setStartDate] = useState<string | null>(selectedStartDate)
  const [endDate, setEndDate] = useState<string | null>(selectedEndDate)
  const [filteredData, setFilteredData] = useState<DeliveryMetrics[] | null>();
  const [currentError, setCurrentError] = useState(undefined)
  const [reason, setReason] = useState(undefined)
  const [triggerRefresh, setTriggerRefresh] = useState(undefined)

  const {
    deliveryMetrics,
    fedexDeliveryMetricsGetError
  } = useAmazonFedExDeliveryMetricsGet(startDate, endDate, userData, triggerRefresh)

  const {
    uploadFile,
    fedexDeliveryMetricsPostError
  } = useAmazonFedExDeliveryMetricsPost(file, userData, setTriggerRefresh)

  const {
    exportDetailedTableXlsx,
    exportTableXlsx
  } = useAmazonDeliveryMetricsXlsxExport(deliveryMetrics)

  // const handleFilter = (selectedReasonId) => {
  //   // No data available to filter; clear the table
  //   if (!fedexDeliveryMetricsGetContext || amazonFedexDeliveryMetricsGetData.length === 0) {
  //     setFilteredData([]); // Set to empty array if there's no data
  //     return;
  //   }
  
  //   // No reason selected, reset to full dataset
  //   if (!selectedReasonId) {
  //     setFilteredData(amazonFedexDeliveryMetricsGetData);
  //     return;
  //   }
  
  //   // Filter by the selected reason
  //   const reason = fedexDelayReason.find((r) => r.id.toString() === selectedReasonId)?.reason;
  
  //   if (reason) {
  //     const filtered = amazonFedexDeliveryMetricsGetData?.filter((item) =>
  //       item.scanEvents.some((event) => event.exceptionDescription === reason)
  //     );
  //     setFilteredData(filtered);
  //   } else {
  //     // No matching reason found; reset to full dataset
  //     setFilteredData(amazonFedexDeliveryMetricsGetData);
  //   }
  // };

  const handleFilter = useCallback((selectedReasonId) => {
    if(!deliveryMetrics){
      //no data to filter on
      return
    }

    if(!selectedReasonId){
      //set to all records if no id provided
      setFilteredData(deliveryMetrics)
    }

    const reason = fedexDelayReason.find((r) => r.id.toString() === selectedReasonId)?.reason;
    if (reason) {
      const filtered = deliveryMetrics?.filter((item) =>
        item.scanEvents?.some((event) => event.exceptionDescription === reason)
      );
      setFilteredData(filtered);
    } else {
      // No matching reason found; reset to full dataset
      setFilteredData(deliveryMetrics);
    }

  }, [deliveryMetrics])

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      setFile(event.target.files[0])
    }
  }

  const handleAmazonDeliveryMetricsDetailedExport = useCallback((e) => {
    if(!exportDetailedTableXlsx) return

    e.preventDefault()
    exportDetailedTableXlsx()
  }, [exportDetailedTableXlsx])

  const handleAmazonDeliveryMetricsTableExport = useCallback((e) => {
    if(!exportTableXlsx) return

    e.preventDefault()
    exportTableXlsx()
  }, [exportTableXlsx])

  // Provide all the state and handlers as context value
  return (
    <>
      <AmazonDeliveryMetricsContext.Provider
        value={{
          selectedStartDate,
          setSelectedStartDate,
          selectedEndDate,
          setSelectedEndDate,
          setStartDate,
          setEndDate,
          currentError,
          deliveryMetrics,
          handleFileChange,
          uploadFile: uploadFile,
          handleAmazonDeliveryMetricsDetailedExport,
          handleAmazonDeliveryMetricsTableExport,
          selectedReason,
          setSelectedReason,
          filteredData,
          handleFilter,
          startDate,
          endDate
        }}
      >
        {children}
      </AmazonDeliveryMetricsContext.Provider>
    </>
  );
};

// Custom hook to use the context
export const useAmazonDeliveryMetricsContext = (): AmazonDeliveryMetricsProviderContext => {
  const context = useContext(AmazonDeliveryMetricsContext);
  if (!context) {
    throw new Error('useAmazonDeliveryMetricsContext must be used within a AmazonDeliveryMetricsProvider');
  }
  return context;
};